import TableComponent from '../../components/table/tableComponent';
import { setChartTitle } from '../../redux/slices/uiSlice';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import './tableContent.css';

function isValidTableShape(data) {
  return (
    data &&
    Array.isArray(data.columns) &&
    Array.isArray(data.rows)
  );
}

export default function TableContent({ data }) {
  const dispatch = useDispatch();

  const tableData = data?.loadError ? null : getTableData(data);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(orientation: landscape)').matches
      : false,
  );

  useEffect(() => {
    const mm = window.matchMedia('(orientation: landscape)');
    const onChange = () => setIsLandscape(mm.matches);
    onChange();
    mm.addEventListener?.('change', onChange);
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      mm.removeEventListener?.('change', onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  useEffect(() => {
    if (tableData?.title) {
      dispatch(setChartTitle(tableData.title));
    } else {
      dispatch(setChartTitle(''));
    }
  }, [dispatch, tableData]);

  if (data?.loadError) {
    return (
      <div className="chart-content-center" role="alert">
        <p style={{ color: '#b91c1c', fontWeight: 600 }}>{data.loadError}</p>
      </div>
    );
  }

  if (!isValidTableShape(tableData)) {
    return (
      <div className="chart-content-center" role="status">
        <p>No hay datos de tabla para mostrar.</p>
      </div>
    );
  }

  const hasRows = tableData.rows.length > 0;

  return (
    <div className="chart-content-center">
      {hasRows ? (
        renderTable(tableData, isLandscape)
      ) : (
        <>
          {renderTable({ ...tableData, rows: [] }, isLandscape)}
          <p role="status" style={{ marginTop: 12, textAlign: 'center' }}>
            No rows to display
          </p>
        </>
      )}
    </div>
  );
}

function getTableData(data) {
  if (isValidTableShape(data)) {
    return data;
  }
  return null;
}

// Function to render a single table
function renderTable(tableData, isLandscape) {
  if (!isValidTableShape(tableData)) {
    return (
      <div className="chart-content-center" role="status">
        <p>No hay datos de tabla para mostrar.</p>
      </div>
    );
  }

  // Transform columns from the table format
  const columns = tableData.columns.map(columnName => {
    const field = getFieldName(columnName);
    return {
      field: field,
      headerName: columnName,
      width: getColumnWidth(columnName),
      type: getColumnType(columnName),
      flex: shouldFlex(columnName) ? 1 : undefined,
    };
  });

  // Transform rows from the table format
  const rows = tableData.rows.map((row, rowIndex) => {
    const transformedRow = { id: rowIndex + 1 };

    // Map each column to the corresponding field
    tableData.columns.forEach(columnName => {
      const field = getFieldName(columnName);
      transformedRow[field] = getRowValue(row, columnName);
    });

    return transformedRow;
  });

  return (
    <div
      className={`table-wrapper ${
        isLandscape ? 'is-landscape' : 'is-portrait'
      }`}
    >
      <TableComponent rows={rows} columns={columns} />
      {tableData.scenario ? <p>{tableData.scenario}</p> : null}
    </div>
  );
}

// Helper function to get field name from column header
function getFieldName(columnName) {
  const fieldMap = {
    No: 'no',
    Fund: 'fund',
    'Index ID': 'indexId',
    'Allocation (%)': 'allocation',
    Inception: 'inception',
    Strategy: 'strategy',
    'Performance (%)': 'performance',
    'Standard Deviation (%)': 'standardDeviation',
    'Sharpe Ratio': 'sharpeRatio',
    'Incremental VAR (%)': 'incrementalVarPct',
    'Component VAR (MM)': 'componentVarMm',
    'Marginal VAR (MM)': 'marginalVarMm',
    'Assets invested (MM)': 'assetsInvestedMm',
    'Min Performance (%)': 'minPerformancePct',
    'Med Performance (%)': 'medPerformancePct',
    'Max Performance (%)': 'maxPerformancePct',
    'Oct-07': 'oct07',
    'Nov-07': 'nov07',
    'Dec-07': 'dec07',
  };

  if (fieldMap[columnName]) {
    return fieldMap[columnName];
  }

  return columnName.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isMonthColumnHeader(columnName) {
  return /^(Oct|Nov|Dec)-/i.test(String(columnName || ''));
}

// Helper function to get column width
function getColumnWidth(columnName) {
  const widthMap = {
    No: 60,
    'Index ID': 90,
    Fund: 200, // Increased width for better mobile readability
    'Allocation (%)': 120,
    Inception: 100,
    Strategy: 200, // Increased width for better mobile readability
    'Performance (%)': 130,
    'Standard Deviation (%)': 150,
    'Sharpe Ratio': 120,
    'Incremental VAR (%)': 130,
    'Component VAR (MM)': 130,
    'Marginal VAR (MM)': 130,
    'Assets invested (MM)': 140,
    'Min Performance (%)': 140,
    'Med Performance (%)': 140,
    'Max Performance (%)': 140,
    'Oct-07': 80,
    'Nov-07': 80,
    'Dec-07': 80,
  };

  if (isMonthColumnHeader(columnName)) {
    return 80;
  }

  return widthMap[columnName] || 120;
}

// Helper function to determine if column should flex
function shouldFlex(columnName) {
  // No columns should flex to ensure fixed widths work properly on mobile
  return false;
}

// Helper function to get column type
function getColumnType(columnName) {
  const numericColumns = [
    'Index ID',
    'Performance (%)',
    'Standard Deviation (%)',
    'Sharpe Ratio',
    'Allocation (%)',
    'Incremental VAR (%)',
    'Component VAR (MM)',
    'Marginal VAR (MM)',
    'Assets invested (MM)',
    'Min Performance (%)',
    'Med Performance (%)',
    'Max Performance (%)',
    'Oct-07',
    'Nov-07',
    'Dec-07',
  ];

  if (numericColumns.includes(columnName) || isMonthColumnHeader(columnName)) {
    return 'number';
  }
  return 'string';
}

// Helper function to get row value for a specific column
function getRowValue(row, columnName) {
  const fieldName = getFieldName(columnName);

  // Monthly columns (dynamic labels from API, e.g. Oct-09)
  if (
    row.monthlyPerformance &&
    Object.prototype.hasOwnProperty.call(row.monthlyPerformance, columnName)
  ) {
    return row.monthlyPerformance[columnName];
  }

  return row[fieldName] !== undefined ? row[fieldName] : '';
}
